import { Injectable } from '@angular/core';

// Define types for better readability and type safety.
interface LegalEntity {
  legalEntityNum: string;
  legalEntityName: string;
}

// An owner/coordinator reference can arrive as a plain id/email string, or as a
// hydrated person object whose email may live under any of several keys.
type OwnerRef = string | { userId?: string; email?: string; emailAddress?: string; userData?: any };

interface Contract {
  contractNum: string;
  contractType: string;
  departments?: string[];
  sites?: { siteNum: string }[];
  legalEntities?: (LegalEntity | string)[]; // Can be object or string
  databaseOrg: string;
  // Owner / coordinator fields for the ownership override. Names drift across
  // code paths: the backend hydrates `primaryOwner`/`secondaryOwner`/`tertiaryOwner`,
  // while the persisted/legacy keys are `*ResponsibleParty`. Both may be present.
  primaryOwner?: OwnerRef;
  secondaryOwner?: OwnerRef;
  tertiaryOwner?: OwnerRef;
  primaryResponsibleParty?: OwnerRef;
  secondaryResponsibleParty?: OwnerRef;
  tertiaryResponsibleParty?: OwnerRef;
  ownerList?: OwnerRef[];
  yourContractCoordinator?: OwnerRef;
}

interface UserPermissions {
  role?: string;
  legalEntities?: (LegalEntity | string)[];
  contractTypes?: string[];
  departments?: string[];
  sites?: { siteNum: string }[];
  databaseOrganizations?: string[];
}

/**
 * A utility service containing pure, stateless functions for filtering data based on permissions.
 *
 * This service is not responsible for fetching or storing data. It is a pure utility that
 * takes data as input and returns a filtered result, making it highly reusable and testable.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsUtilityService {
  /**
   * Filters a list of contracts based on the provided user permissions.
   * This is a pure function and does not rely on any internal state.
   * @param contractsFromServer The list of contracts to filter.
   * @param permissions The permissions object to use for filtering.
   * @param currentUserEmail The current logged-in user's email for ownership checking (optional).
   * @returns The filtered list of contracts.
   */
  filterContracts(contractsFromServer: Contract[], permissions: UserPermissions, currentUserEmail?: string | null): Contract[] {
    if (!permissions) {
      return contractsFromServer;
    }

    let filteredContracts = contractsFromServer.filter(contract => {

      // --- Ownership Override ---
      // If the user is an owner/coordinator of this contract, they can always see it
      if (currentUserEmail && this.isUserOwnerOfContract(contract, currentUserEmail)) {
        return true;
      }

      // --- Filter by Legal Entities ---
      if (permissions.legalEntities && permissions.legalEntities.length > 0) {
        const allowedLegalEntityList = permissions.legalEntities.map(le => this.refId(le, 'legalEntityNum'));
        const contractHasAllowedLegalEntity = (contract.legalEntities || []).some(contractLE => {
          const legalEntityNum = this.refId(contractLE, 'legalEntityNum');
          return legalEntityNum != null && allowedLegalEntityList.includes(legalEntityNum);
        });
        if (!contractHasAllowedLegalEntity) {
          return false;
        }
      }

      // --- Filter by Contract Types ---
      if (permissions.contractTypes && permissions.contractTypes.length > 0) {
        if (!permissions.contractTypes.includes(contract.contractType)) {
          return false;
        }
      }

      // --- Filter by Departments ---
      let departmentsToInclude = permissions.departments ? [...permissions.departments] : [];
      const excludeLegal = departmentsToInclude.includes('Legal_Exc');
      if (excludeLegal) {
        departmentsToInclude = departmentsToInclude.filter(dep => dep !== 'Legal_Exc');
        if ((contract.departments || []).includes('Legal')) {
          return false; // Exclude 'Legal' if the flag is set.
        }
      }

      if (departmentsToInclude.length > 0) {
        if (!(contract.departments || []).some(dep => departmentsToInclude.includes(dep))) {
          return false;
        }
      }

      // --- Filter by Sites ---
      if (permissions.sites && permissions.sites.length > 0) {
        const allowedSites = permissions.sites.map(s => this.refId(s, 'siteNum'));
        const contractHasAllowedSite = (contract.sites || []).some(site => {
          const siteNum = this.refId(site, 'siteNum');
          return siteNum != null && allowedSites.includes(siteNum);
        });
        if (!contractHasAllowedSite) {
          return false;
        }
      }

      // --- Filter by Database Organizations ---
      if (permissions.databaseOrganizations && permissions.databaseOrganizations.length > 0) {
        if (!permissions.databaseOrganizations.includes(contract.databaseOrg)) {
          return false;
        }
      }

      return true;
    });

    // Use a Set to remove duplicates based on a unique identifier like contractNum.
    const uniqueContracts = Array.from(new Set(filteredContracts.map(contract => contract.contractNum)))
      .map(contractNum => filteredContracts.find(contract => contract.contractNum === contractNum)!);

    return uniqueContracts;
  }

  /**
   * Filters contracts for a VIEW-ONLY (or not-yet-permissioned) user.
   *
   * Unlike privileged roles, a viewer with no site/legal-entity/etc. limits must
   * NOT see every contract — empty limits mean "nothing extra," not "see all."
   * A viewer sees:
   *   - every contract they OWN (primary/secondary/tertiary owner or coordinator —
   *     same definition as the ownership override), PLUS
   *   - any contracts matching site/legal-entity/contract-type/department/database-org
   *     limits an admin has explicitly assigned to them.
   *
   * So an unassigned new viewer sees only the contracts they own, and an admin can
   * widen that by assigning metadata limits.
   *
   * @param contractsFromServer The list of contracts to filter.
   * @param permissions The viewer's permissions (may have empty/no limits).
   * @param currentUserEmail The current user's email for ownership checking.
   */
  filterContractsForViewer(contractsFromServer: Contract[], permissions: UserPermissions | null, currentUserEmail?: string | null): Contract[] {
    const contracts = contractsFromServer || [];

    // No explicit limits assigned → owned-only (the key difference from privileged roles).
    if (this.hasNoMetadataLimits(permissions)) {
      const email = currentUserEmail || '';
      return contracts.filter(contract => this.isUserOwnerOfContract(contract, email));
    }

    // Limits assigned → reuse the standard filter, which already returns
    // (contracts matching the limits) ∪ (contracts the user owns, via the override).
    return this.filterContracts(contracts, permissions as UserPermissions, currentUserEmail);
  }

  /**
   * True when a permissions object carries no site/legal-entity/contract-type/
   * department/database-org limits (all empty or absent). Null permissions count
   * as "no limits."
   */
  private hasNoMetadataLimits(permissions: UserPermissions | null | undefined): boolean {
    if (!permissions) return true;
    return (!permissions.legalEntities || permissions.legalEntities.length === 0) &&
           (!permissions.contractTypes || permissions.contractTypes.length === 0) &&
           (!permissions.departments || permissions.departments.length === 0) &&
           (!permissions.sites || permissions.sites.length === 0) &&
           (!permissions.databaseOrganizations || permissions.databaseOrganizations.length === 0);
  }

  /**
   * Checks if a single contract has permissions to be viewed by the user.
   * @param contract The contract to check.
   * @param permissions The permissions object to use for checking.
   * @returns A boolean indicating if the user has permissions to view the contract.
   */
  userHasPermissionsToViewContract(contract: Contract, permissions: UserPermissions): boolean {
    return this.filterContracts([contract], permissions).length > 0;
  }

  /**
   * Filters a list of legal entities based on the permissions.
   * @param allLegalEntities The full list of legal entities.
   * @param permissions The permissions object to use for filtering.
   * @returns The filtered list of legal entities.
   */
  filterLegalEntitiesByPermissions(allLegalEntities: LegalEntity[], permissions: UserPermissions): LegalEntity[] {
    if (!permissions || !permissions.legalEntities || permissions.legalEntities.length === 0) {
      return allLegalEntities;
    }

    const allowedLegalEntityNames = permissions.legalEntities.map(le => typeof le === 'object' ? le.legalEntityName : le);
    return allLegalEntities.filter(le => allowedLegalEntityNames.includes(le.legalEntityName));
  }

  /**
   * Filters a list of sites based on the permissions.
   * @param allSites The full list of sites.
   * @param permissions The permissions object to use for filtering.
   * @returns The filtered list of sites.
   */
  filterSitesByPermissions(allSites: { siteNum: string, name: string }[], permissions: UserPermissions): { siteNum: string, name: string }[] {
    if (!permissions || !permissions.sites || permissions.sites.length === 0) {
      return allSites;
    }

    const allowedSiteNums = permissions.sites.map(s => this.refId(s, 'siteNum'));
    return allSites.filter(site => {
      const siteNum = this.refId(site, 'siteNum');
      return siteNum != null && allowedSiteNums.includes(siteNum);
    });
  }

  /**
   * Checks if the current user is an owner or coordinator of the contract.
   *
   * Ownership override (per Ray's "Internal Coordinator/All Owners" rule): a user
   * listed as the internal contract coordinator or any of the primary/secondary/
   * tertiary owners can always see the contract, regardless of their site / legal
   * entity / contract-type / department / database-org permissions. This intentionally
   * lets a user "violate" their own visibility rules for objects they own — it
   * eliminates a large class of "why can't I see my own contract" helpdesk calls.
   *
   * Resilient to data drift: owner references may be id/email strings or hydrated
   * person objects (email under `email`/`emailAddress`/`userId`/`userData.email`),
   * and the same logical owner can be stored under either the hydrated `*Owner`
   * keys or the legacy `*ResponsibleParty` keys. We check all of them, plus the
   * `ownerList[]` array, and match case-insensitively.
   *
   * @param contract The contract to check.
   * @param userEmail The current user's email.
   * @returns True if the user is an owner/coordinator of this contract.
   */
  /**
   * Normalize a metadata reference to its id, whichever shape it arrives in.
   *
   * These lists are genuinely polymorphic. `contract.sites` and
   * `contract.legalEntities` are stored in DynamoDB as **bare id strings** and are
   * swapped for full objects by RehydrationService at read time — so this filter
   * can see either, depending on whether hydration ran and whether it succeeded.
   * `permissions.sites` is stored the other way round: full Site objects.
   *
   * It can also see **null**. Hydration resolves each id through
   * `SiteService.getSiteObject()`, which returns null for an id it can't find, and
   * the results are collected with `toArray()` without dropping the nulls. So a
   * contract can arrive here with `sites: [null]`.
   *
   * That is why this exists rather than an inline `typeof x === 'object'` check:
   * `typeof null === 'object'` is TRUE in JavaScript, so the obvious guard reads a
   * property off null and throws. Thrown here, the error escapes the `.filter()`
   * into ContractService's combineLatest subscriber and kills the subscription —
   * visibleContracts would stop updating entirely rather than degrade. An
   * unresolvable reference must simply not match.
   *
   * @returns the id string, or null when the reference is absent/unresolvable.
   */
  private refId(ref: any, idField: 'siteNum' | 'legalEntityNum'): string | null {
    if (ref == null) return null;
    if (typeof ref === 'string') return ref || null;
    if (typeof ref === 'object') {
      const v = ref[idField];
      return typeof v === 'string' && v ? v : null;
    }
    return null;
  }

  private isUserOwnerOfContract(contract: Contract, userEmail: string): boolean {
    const ownerRefs: (OwnerRef | undefined)[] = [
      contract.primaryOwner,
      contract.secondaryOwner,
      contract.tertiaryOwner,
      contract.primaryResponsibleParty,
      contract.secondaryResponsibleParty,
      contract.tertiaryResponsibleParty,
      contract.yourContractCoordinator,
      ...(Array.isArray(contract.ownerList) ? contract.ownerList : [])
    ];

    return ownerRefs.some(ref => this.ownerRefMatchesEmail(ref, userEmail));
  }

  /**
   * True when a single owner/coordinator reference resolves to `userEmail`.
   * Handles both the plain-string (id/email) and hydrated-object shapes, and
   * compares emails case-insensitively (trimmed) so casing differences from the
   * SSO/IdP email claim don't drop a legitimate match.
   */
  private ownerRefMatchesEmail(ref: OwnerRef | undefined | null, userEmail: unknown): boolean {
    // Defensive: callers occasionally pass a non-string (e.g. a whole user object
    // instead of its email). Never let that crash the contract filter — just no match.
    if (!ref || typeof userEmail !== 'string' || !userEmail) {
      return false;
    }

    const target = userEmail.toLowerCase().trim();
    const eq = (candidate: unknown): boolean =>
      typeof candidate === 'string' && candidate.toLowerCase().trim() === target;

    if (typeof ref === 'string') {
      return eq(ref);
    }

    return eq(ref.userId) || eq(ref.email) || eq(ref.emailAddress) || eq(ref.userData?.email);
  }
}
